import _ from "lodash";
import React from "react";

import { BehavioralGoalsRating } from "@/components/BehavioralGoalsRating";
import { BehavioralMentalStatus } from "@/features/note-content/components/BehavioralMentalStatus";
import { BehavioralSymptomTrend } from "@/features/note-content/components/BehavioralSymptomTrend";
import CardSecondary from "@/components/UI/Card/CardSecondary";
import { EmptyCard } from "@/component-library/EmptyCard/EmptyCard";
import { ExpandingSelect } from "@/components/UI/ExpandingSelect";
import { ExpandingTextArea } from "@/components/UI/ExpandingTextArea";
import { makeCnaiProps } from "@/lib/helpers/clinically-ai";
import { RATING_OPTION_LABEL_MAP } from "@/constants/rating";
import { TextArea } from "@/components/UI/StyledInput";

export const BehavioralSymptomTrends = (componentProps) => {
  let { noteEditHandler, state } = componentProps;
  let { progressNote } = state;

  return (
    <>
      {/* CNAI disabled in-session (enable: false) — this live-call widget shares its field id with
          the post-call note, where tagging is active instead */}
      <ExpandingTextArea
        label="How are you?"
        onChange={(e) => {
          noteEditHandler("symptom_trend_notes", e.target.value);
        }}
        textareaProps={makeCnaiProps({
          enable: false,
          format: "paragraph",
          id: "symptom-trend-notes",
          name: "Symptom Trends/Review",
          type: "string",
        })}
        value={progressNote.content.symptom_trend_notes}
      />
      <div className="m-3">
        <BehavioralSymptomTrend
          editable={true}
          editHandler={noteEditHandler}
          note={progressNote}
          patientAnswersEditable={false}
        />
      </div>
    </>
  );
};

export const BehavioralExploration = (componentProps) => {
  let { noteEditHandler, state } = componentProps;
  let { progressNote } = state;

  const editHandlerBuilder = (sectionLabel, deeperLabels = []) => {
    return (e, index, key) => {
      let original = progressNote.content[sectionLabel];
      let editCopyClone = _.cloneDeep(original);

      let deeperObject = editCopyClone;

      deeperLabels.forEach((label) => {
        deeperObject = deeperObject[label];
      });
      deeperObject[index][key] = !deeperObject[index][key];

      if (index === "none") {
        Object.entries(deeperObject).forEach(([sectionKey, value]) => {
          if (sectionKey !== "none") {
            value[key] = false;
          }
        });
      } else {
        if (deeperObject["none"]) {
          deeperObject["none"][key] = false;
        }
      }

      noteEditHandler(sectionLabel, editCopyClone);
    };
  };

  return (
    <>
      <ExpandingTextArea
        label="Notes"
        onChange={(e) => {
          noteEditHandler("exploration_notes", e.target.value);
        }}
        value={progressNote.content.exploration_notes}
      />
      <ExpandingSelect
        className="m-3 grey-container"
        collapsible={false}
        disabled
        label="Changes/Updates"
        labelClass="tw:font-bold"
        onChange={editHandlerBuilder("changes")}
        options={progressNote.content.changes}
      />

      <ExpandingSelect
        className="m-3 grey-container"
        collapsible={false}
        disabled
        label="Health Care Use"
        labelClass="tw:font-bold"
        onChange={editHandlerBuilder("health_care_use")}
        options={progressNote.content.health_care_use}
      />

      <BehavioralMentalStatus
        editable={true}
        editHandler={noteEditHandler}
        isCnaiEnabled={false}
        note={progressNote}
        showTitle={false}
      />
    </>
  );
};

export const BehavioralInterventions = (componentProps) => {
  let { noteEditHandler, state } = componentProps;
  let { progressNote } = state;
  let goalsObjectivesProgress = progressNote.content.goals_objectives_progress || [];

  const handleChange = (e, index, key, isNumber = false) => {
    let editCopyClone = _.cloneDeep(goalsObjectivesProgress);

    editCopyClone[index][key] = isNumber ? parseInt(e.target.value) : e.target.value;
    noteEditHandler("goals_objectives_progress", editCopyClone);
  };

  const renderGoalsAndObjectives = () => {
    return (
      <>
        <div
          className="row justify-content-between mx-0 py-2 mb-2 text-white"
          style={{ backgroundColor: "#0CD0A7", borderRadius: "5px 5px 0 0" }}
        >
          <p className="col-lg-5 tw:font-bold">Goals and Objectives</p>
          <div className="col-lg-7 d-flex justify-content-start">
            <div className="col-12 d-flex justify-content-start">
              {Object.values(RATING_OPTION_LABEL_MAP).map((optionLabel) => (
                <div className="w-25 fs-14 text-center tw:font-bold" key={optionLabel}>
                  <div className="m-auto" style={{ width: 50 }}>
                    {optionLabel}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {goalsObjectivesProgress.map((progress, index) => {
          return (
            <>
              <div className="row px-3 p-1 justify-content-between">
                <div className="col-lg-5 tw:font-bold">{progress.label} - Intervention</div>
                <div className="col-lg-7 d-flex justify-content-start">
                  <BehavioralGoalsRating
                    isEditing={true}
                    onClick={(e) => handleChange(e, index, "answer", true)}
                    optionLabelMap={RATING_OPTION_LABEL_MAP}
                    progress={progress}
                  />
                </div>
              </div>
              <div className="mx-auto t-2 px-3 d-flex justify-content-between mb-2">
                <TextArea
                  classes="large"
                  onChange={(e) => handleChange(e, index, "actions")}
                  spanClasses="w-100"
                  value={goalsObjectivesProgress[index].actions}
                />
              </div>
            </>
          );
        })}
      </>
    );
  };

  return (
    <CardSecondary className="m-3">
      {goalsObjectivesProgress.length ? (
        renderGoalsAndObjectives()
      ) : (
        <EmptyCard text="No goals or objectives." />
      )}
    </CardSecondary>
  );
};

export const BehavioralActions = (componentProps) => {
  let { noteEditHandler, state } = componentProps;
  let { progressNote } = state;
  let goalsObjectivesProgress = progressNote.content.goals_objectives_progress || [];

  const handleChange = (e, index, key, isNumber = false) => {
    let editCopyClone = _.cloneDeep(goalsObjectivesProgress);

    editCopyClone[index][key] = isNumber ? parseInt(e.target.value) : e.target.value;
    noteEditHandler("goals_objectives_progress", editCopyClone);
  };

  return goalsObjectivesProgress.map((progress, index) => {
    return (
      <CardSecondary className="m-3" key={progress.label + index}>
        <div className="row px-3 pt-3 pb-1 justify-content-between">
          <div className="col-lg-5 tw:font-bold">{progress.label} - Plan</div>
        </div>

        <div className="mx-auto t-2 px-3 pt-1 pb-3 d-flex justify-content-between mb-2">
          <TextArea
            classes="large"
            onChange={(e) => handleChange(e, index, "plan")}
            spanClasses="w-100"
            value={goalsObjectivesProgress[index].plan}
          />
        </div>
      </CardSecondary>
    );
  });
};
